import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from "react";
import { formatContactPhone, phoneCaret } from "../../shared/contact-phone";

type EditableVariant = "standard" | "document" | "utility";

export function InputAffix({ children, suffix }: { children: ReactNode; suffix: ReactNode }) {
  return <span className="affix">{children}<i aria-hidden="true">{suffix}</i></span>;
}

export function Field({ error, hint, label, fieldClassName = "", suffix, variant = "standard", ...props }: InputHTMLAttributes<HTMLInputElement> & { error?: string; hint?: string; label: string; fieldClassName?: string; suffix?: ReactNode; variant?: EditableVariant }) {
  const id = useId();
  const hintId = `${id}-hint`;
  const labelId = `${id}-label`;
  const input = <input {...props} onChange={event => {
    if (props.type === "tel" && !(event.nativeEvent as InputEvent).isComposing) {
      const node = event.currentTarget, value = node.value, start = node.selectionStart, end = node.selectionEnd;
      const formatted = formatContactPhone(value);
      node.value = formatted;
      if (value !== formatted && start !== null && end !== null) node.setSelectionRange(phoneCaret(value, formatted, start), phoneCaret(value, formatted, end));
    }
    props.onChange?.(event);
  }} id={id} className={`field-control ${variant === "document" ? "" : "input"} ${props.className ?? ""}`.trim()} aria-labelledby={props["aria-labelledby"] ?? (props["aria-label"] ? undefined : labelId)} aria-describedby={error || hint ? hintId : props["aria-describedby"]} aria-invalid={error ? true : props["aria-invalid"]} />;
  return (
    <label className={`field ${fieldClassName}`.trim()} htmlFor={id}>
      <span id={labelId}>{label}</span>
      {suffix ? <InputAffix suffix={suffix}>{input}</InputAffix> : input}
      {error || hint ? <small id={hintId} className={error ? "fld-error" : "field-hint"}>{error || hint}</small> : null}
    </label>
  );
}

export function SelectField({ children, hint, label, fieldClassName = "", variant = "standard", ...props }: SelectHTMLAttributes<HTMLSelectElement> & { hint?: string; label: string; fieldClassName?: string; variant?: EditableVariant }) {
  const id = useId();
  const hintId = `${id}-hint`;
  const labelId = `${id}-label`;
  return (
    <label className={`field ${fieldClassName}`.trim()} htmlFor={id}>
      <span id={labelId}>{label}</span>
      <select {...props} id={id} className={`field-control ${variant === "document" ? "" : "input"} ${props.className ?? ""}`.trim()} aria-labelledby={props["aria-labelledby"] ?? (props["aria-label"] ? undefined : labelId)} aria-describedby={hint ? hintId : props["aria-describedby"]}>{children}</select>
      {hint ? <small id={hintId} className="field-hint">{hint}</small> : null}
    </label>
  );
}

export function Checkbox({ label, ...props }: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { label: string }) {
  return <label className="checkbox"><input {...props} type="checkbox"/><span>{label}</span></label>;
}

export function Toggle({ checked, label, onChange, disabled, role, inputRef }: { checked: boolean; label: string; disabled?: boolean; role?: "switch"; inputRef?: React.Ref<HTMLInputElement>; onChange(checked: boolean): void }) {
  return <label className="toggle"><input ref={inputRef} type="checkbox" role={role} checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} /><span className="toggle-track" aria-hidden="true" /><span>{label}</span></label>;
}

export function SegmentedControl<T extends string>({ label, onChange, options, value, radio = false, className = "" }: { label: string; onChange(value: T): void; options: readonly { label: string; value: T }[]; value: T; radio?: boolean; className?: string }) {
  return <div className={`segmented-control ${className}`.trim()} role={radio ? "radiogroup" : "group"} aria-label={label}>{options.map((option, index) => <button key={option.value} type="button" role={radio ? "radio" : undefined} aria-checked={radio ? option.value === value : undefined} aria-pressed={radio ? undefined : option.value === value} tabIndex={radio ? option.value === value ? 0 : -1 : undefined} onClick={() => onChange(option.value)} onKeyDown={event => {
    if (!radio || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : (index + (/Right|Down/.test(event.key) ? 1 : -1) + options.length) % options.length;
    onChange(options[next]!.value);
    (event.currentTarget.parentElement?.children[next] as HTMLElement)?.focus();
  }}>{option.label}</button>)}</div>;
}
